import { motion } from "framer-motion";
import React from "react";

interface ModalProps {
  message?: string; // Optional: for displaying a message
  onConfirm?: () => void; // Optional: for the confirm button
  onCancel?: () => void; // Optional: for the cancel button
  children?: React.ReactNode; // Support custom content
  onClose?: () => void; // Support closing the modal
}

const Modal = ({
  message,
  onConfirm,
  onCancel,
  children,
  onClose,
}: ModalProps) => {
  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.3, ease: "easeOut" },
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      transition: { duration: 0.3, ease: "easeIn" },
    },
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && onClose) {
      onClose(); // Close on backdrop click
    }
  };

  return (
    <motion.div
      className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={handleBackdropClick}
    >
      <motion.div
        className="bg-white p-4 sm:p-6 md:p-8 rounded-2xl shadow-lg max-w-[90%] sm:max-w-md md:max-w-lg w-full text-center relative border-2 border-primary"
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
      >
        {/* Decorative Elements (Static) */}
        <div className="absolute -top-4 -left-4 w-8 sm:w-10 h-8 sm:h-10 bg-primary rounded-full opacity-80" />
        <div className="absolute -bottom-4 -right-4 w-8 sm:w-10 h-8 sm:h-10 bg-secondary rounded-full opacity-80" />

        {/* Close Button */}
        {onClose && (
          <motion.button
            onClick={onClose}
            className="absolute top-2 right-2 text-navy hover:text-primary focus:outline-none"
            whileHover={{ scale: 1.2 }}
            whileTap={{ scale: 0.9 }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 sm:h-7 w-6 sm:w-7"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </motion.button>
        )}

        {/* Content */}
        {children ? (
          <div className="py-2 sm:py-4">{children}</div>
        ) : (
          <>
            <p
              className="text-lg sm:text-xl font-bold text-navy mb-4 sm:mb-6"
              style={{ fontFamily: "Baloo 2", fontWeight: 700 }}
            >
              {message}
            </p>
            <div className="flex flex-row flex-wrap justify-center gap-3 sm:gap-4">
              {onConfirm && (
                <motion.button
                  onClick={onConfirm}
                  className="bg-primary text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold shadow-md text-sm sm:text-base"
                  whileHover={{
                    scale: 1.05,
                    boxShadow: "0 0 15px rgba(255, 95, 0, 0.5)",
                  }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  Áno
                </motion.button>
              )}
              {onCancel && (
                <motion.button
                  onClick={onCancel}
                  className="bg-gray-200 text-navy px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold shadow-md text-sm sm:text-base"
                  whileHover={{
                    scale: 1.05,
                    boxShadow: "0 0 15px rgba(0, 0, 0, 0.2)",
                  }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  Späť
                </motion.button>
              )}
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
};

export default Modal;
